import React from 'react';
import { CopyableCell } from '../../components/Common/Components/CopyableCell';
import { formatNumber } from '../../components/Common/Utilities';
import { loadTab, storeTab } from './StatusTabs';

// New UI for the Invoices and Archive lists (docs/ui_redesign/14-screen-concepts.md, "Invoices and Archive", option A).
// Display only: the lists keep their data, columns, filters and handlers. Classic never calls this.

// Status tabs, named and grouped like the dashboard's queues (redesign/dashboard/pipelineModel.js). "Ready to post" is
// the set Classic's Bulk Post accepts (its approval link is Post to ERP System). A row no tab claims (for example status 175) is listed under All only.
export const INVOICE_QUEUES = [
	{ key: 'all', label: 'All' },
	{ key: 'unmatched', label: 'Unmatched' },
	{ key: 'correct', label: 'Needs correction' },
	{ key: 'approve', label: 'Pending approval' },
	{ key: 'ready', label: 'Ready to post' },
	{ key: 'failed', label: 'Post failed', tone: 'bad' },
	{ key: 'posted', label: 'Posted' }
];
const READY_LINKS = ['Post to ERP System', 'Ready to Post'];

export function invoiceQueue(row) {
	if (!row) return null;
	const s = Number(row.invoiceDocumentStatusId);
	if (s === 180) return 'failed';
	if (s === 170) return 'posted';
	if (s > 0 && s < 40) return 'unmatched';
	if (s === 40 || s === 41 || s === 60) return 'correct';
	if (READY_LINKS.includes(row.approvalLinkText) || row.status === 'Ready to Post') return 'ready';
	if (s === 50 || (s >= 70 && s <= 160 && s % 10 === 0)) return 'approve'; // approved levels short of the last, and rejections, as on the dashboard
	return null;
}

export function queueTabs(rows) {
	const counts = {};
	(rows || []).forEach(r => { const q = invoiceQueue(r); if (q) counts[q] = (counts[q] || 0) + 1; });
	return INVOICE_QUEUES.map(q => ({ ...q, count: q.key === 'all' ? (rows || []).length : counts[q.key] || 0 }));
}

export function rowsForQueue(rows, key) {
	if (!key || key === 'all' || !Array.isArray(rows)) return rows;
	return rows.filter(r => invoiceQueue(r) === key);
}

const TAB_KEY = m => `apworks.invoiceQueue.${m}`;
export const savedQueue = modelDefId => loadTab(TAB_KEY(modelDefId), INVOICE_QUEUES.map(q => q.key));
export const saveQueue = (modelDefId, key) => storeTab(TAB_KEY(modelDefId), key);

// "V300 - Brightline Outdoor / MAIN" -> name, then code and site. Anything else is shown as it is.
export function splitVendor(text) {
	const s = String(text || '').trim();
	const m = s.match(/^(\S+)\s+-\s+(.+?)(?:\s+\/\s+([^/]+))?$/); // a code has no spaces but may have hyphens
	if (!m) return { name: s, meta: '' };
	return { name: m[2], meta: [m[1], m[3]].filter(Boolean).join(' · ') };
}

export function VendorCell({ value }) {
	const v = splitVendor(value);
	return (
		<CopyableCell value={value || ''} tooltipTitle={value || ''} style={{ display: 'block', minWidth: 0, width: '100%' }}>
			<span className="apw2-two"><b>{v.name}</b>{v.meta ? <small>{v.meta}</small> : null}</span>
		</CopyableCell>
	);
}

// Classic lists several clients as "A|B|C" and shows "A..."; here the first client and how many more, all in the tooltip.
function ClientCell({ value }) {
	const raw = value || '';
	const list = raw.split('|').map(c => c.trim()).filter(Boolean);
	return (
		<CopyableCell value={raw} tooltipTitle={<span style={{ whiteSpace: 'pre-line' }}>{list.join('\n')}</span>} tooltipPlacement="top"
			style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
			{list[0] || ''}{list.length > 1 ? <span className="apw2-more"> +{list.length - 1}</span> : null}
		</CopyableCell>
	);
}

const amountCell = (field, withCurrency) => ({ row }) => {
	const shown = formatNumber(row[field]);
	const full = shown + (row.currencyCode ? ' ' + row.currencyCode : '');
	return (
		<CopyableCell value={full} tooltipTitle={full} style={{ display: 'block', textAlign: 'right', width: '100%' }}>
			{withCurrency ? <><b className="apw2-amt">{shown}</b><span className="apw2-ccy">{row.currencyCode || ''}</span></> : shown}
		</CopyableCell>
	);
};

// Column order and sizes: who and what first, then status, the money, then dates; the rest follows to the right.
// Sizes are floors that fit the first nine or ten columns at 1440 px; wider screens share out the rest.
const ORDER = {
	invoices: ['vendor', 'payTo', 'invoiceNumber', 'clientNameCode', 'advertiserProduct', 'status', 'invoiceUserStatus', 'serviceTermStartDate', 'totalSpot',
		'netAmount', 'taxAmount', 'totalAmount', 'invoiceDate', 'dueDate', 'dateUpdated', 'mediaType', 'invoiceSource', 'contractNumber', 'modifyDate', 'transactionId',
		'fileName', 'invoiceDocumentFileLocation'],
	archive: ['vendorCodeName', 'payTo', 'invoiceNumber', 'clientNameCode', 'advertiserProduct', 'status', 'totalSpot', 'netAmount', 'taxAmount', 'totalInvoiceAmount',
		'invoiceDate', 'dueDate', 'processedDate', 'mediaType', 'fileName', 'invoiceDocumentFileLocation']
};
const SIZE = {
	vendor: [1.6, 190], vendorCodeName: [1.6, 190], payTo: [1.6, 190], invoiceNumber: [0.9, 104], clientNameCode: [1.1, 124], advertiserProduct: [1.2, 150],
	status: [1.3, 150], invoiceUserStatus: [0.9, 104], serviceTermStartDate: [0.9, 120], totalSpot: [0.6, 80], netAmount: [0.8, 96], taxAmount: [0.7, 84],
	totalAmount: [1, 124], totalInvoiceAmount: [1, 124], invoiceDate: [0.8, 96], dueDate: [0.8, 96], dateUpdated: [0.8, 96], processedDate: [0.8, 96],
	modifyDate: [0.8, 96], transactionId: [0.9, 110], fileName: [1.4, 200], invoiceDocumentFileLocation: [1.4, 200], mediaType: [0.8, 100], actions: [0.9, 176]
};
const HEADER = { netAmount: 'Net', taxAmount: 'Tax', totalAmount: 'Total', totalInvoiceAmount: 'Total', dateUpdated: 'Processed', processedDate: 'Processed', modifyDate: 'Modified' };

export function arrangeListColumns(columns, list, { dateFormatter, dateTimeFormatter } = {}) {
	if (!Array.isArray(columns)) return columns;
	const order = ORDER[list] || [];
	const rank = c => { const i = order.indexOf(c.field); return i < 0 ? order.length : i; };
	const sorted = columns.map((c, i) => [c, i]).sort((a, b) => (rank(a[0]) - rank(b[0])) || (a[1] - b[1])).map(x => x[0]);
	return sorted.map(c => {
		if (!c || !SIZE[c.field]) return c;
		const [flex, minWidth] = SIZE[c.field];
		const out = { ...c, flex, minWidth, width: undefined, apw2Sized: true };
		if (HEADER[c.field]) out.headerName = HEADER[c.field];
		if (c.field === 'vendor' || c.field === 'vendorCodeName') out.renderCell = p => <VendorCell value={p.value} />;
		if (c.field === 'clientNameCode') out.renderCell = p => <ClientCell value={p.row.clientNameCode} />;
		if (c.field === 'netAmount' || c.field === 'taxAmount') out.renderCell = amountCell(c.field, false);
		if (c.field === 'totalAmount' || c.field === 'totalInvoiceAmount') out.renderCell = amountCell(c.field, true);
		if ((c.field === 'dateUpdated' || c.field === 'processedDate') && dateFormatter) {
			const day = v => (v ? dateFormatter.format(new Date(v)) : '');
			out.valueFormatter = ({ value }) => day(value);
			out.renderCell = p => {
				const d = day(p.value);
				const full = p.value && dateTimeFormatter ? dateTimeFormatter.format(new Date(p.value)) : d;
				return <CopyableCell value={full} tooltipTitle={full} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d}</CopyableCell>;
			};
		}
		return out;
	});
}

// R2 and R21 (docs/ui_redesign/18-ux-review.md): the delete confirm and its result name the invoices.
const numberOf = r => (r && (r.invoiceNumber || '').toString().trim()) || (r && r.invoiceDocumentId ? `#${r.invoiceDocumentId}` : '');
export function listNumbers(numbers, max = 3) {
	const n = numbers.filter(Boolean);
	if (n.length <= 1) return n[0] || '';
	if (n.length <= max) return n.slice(0, -1).join(', ') + ' and ' + n[n.length - 1];
	return n.slice(0, max).join(', ') + ` and ${n.length - max} more`;
}
export function deleteCopy(rows) {
	const list = (rows || []).filter(Boolean);
	const one = list.length === 1;
	return {
		title: one ? `Delete invoice ${numberOf(list[0])}?` : `Delete ${list.length} invoices?`,
		body: `${listNumbers(list.map(numberOf))} will be removed. This can’t be undone.`,
		confirm: one ? 'Delete invoice' : 'Delete invoices'
	};
}
// The result lists each invoice by number: the ones that failed with the reason, then the ones deleted.
export function deleteResultLines(result, rows) {
	const byId = new Map((rows || []).filter(Boolean).map(r => [r.invoiceDocumentId, numberOf(r)]));
	const label = item => byId.get(item.invoiceDocumentId) || (item.invoiceDocumentId ? `#${item.invoiceDocumentId}` : '');
	const failed = ((result && result.failed) || []).map(item => {
		const n = label(item);
		const why = (item.errorMessage || 'Couldn’t be deleted').replace(new RegExp('^' + n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ':\\s*'), '');
		return { tone: 'bad', text: n ? `${n}: ${why}` : why };
	});
	const deleted = ((result && result.deleted) || []).map(item => ({ tone: 'ok', text: `${label(item) || 'Invoice'} deleted` }));
	return {
		summary: `${deleted.length} deleted, ${failed.length} not deleted`,
		lines: [...failed, ...deleted]
	};
}
